import {
  type CalendarDate,
  getLocalTimeZone,
  today,
} from '@internationalized/date';
import { ChevronLeft, ChevronRight } from '@untitledui/icons';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import { Fragment, useContext, useMemo, useState } from 'react';
import type {
  CalendarProps as AriaCalendarProps,
  DateValue,
} from 'react-aria-components';
import {
  Calendar as AriaCalendar,
  CalendarContext as AriaCalendarContext,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridBody as AriaCalendarGridBody,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarHeaderCell as AriaCalendarHeaderCell,
  CalendarStateContext as AriaCalendarStateContext,
  DateFieldContext as AriaDateFieldContext,
  Heading as AriaHeading,
  useSlottedContext,
} from 'react-aria-components';

import { Button } from '@/lib/base/buttons/button';
import { cx } from '@/utils/cx.ts';

import { CalendarCell } from './cell.tsx';
import { DateInput } from './date-input.tsx';

export const CalendarContextProvider = ({ children }: PropsWithChildren) => {
  const [value, onChange] = useState<DateValue | null>(null);
  const [focusedValue, onFocusChange] = useState<DateValue | undefined>();

  return (
    <AriaCalendarContext
      value={{ value, onChange, focusedValue, onFocusChange }}
    >
      {children}
    </AriaCalendarContext>
  );
};

const PresetButton = ({
  value,
  children,
  onClick,
  ...props
}: HTMLAttributes<HTMLButtonElement> & { value: CalendarDate }) => {
  const context = useContext(AriaCalendarStateContext);

  if (!context) {
    throw new Error('Preset must be used within a Calendar component');
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    context.setValue(value);
    context.setFocusedDate(value);
    // Call the consumer-provided onClick if present
    onClick?.(e);
  };

  return (
    <Button
      {...props}
      // It's important to give `null` explicitly to the `slot` prop
      // otherwise the button will throw an error due to not using one of
      // the required slots inside the Calendar component.
      // Passing `null` will tell the button to not use a slot context.
      slot={null}
      size='md'
      color='secondary'
      onClick={handleClick}
    >
      {children}
    </Button>
  );
};

interface CalendarProps extends AriaCalendarProps<DateValue> {
  /** The dates to highlight. */
  highlightedDates?: DateValue[];
  /**
   * Hide the editable `DD/MM/YYYY` field + "Today" shortcut row. The field is
   * redundant when the calendar is paired with another control (e.g. a time
   * field in the same popover) and removing it meaningfully shortens the
   * popover so it fits on small screens. Defaults to showing the row.
   */
  hideDateInput?: boolean;
  /**
   * Tighten vertical rhythm (smaller gaps + inter-row spacing) so the calendar
   * takes less height. Used alongside `hideDateInput` for compact popovers.
   */
  compact?: boolean;
}

export const Calendar = ({
  highlightedDates,
  hideDateInput = false,
  compact = false,
  className,
  ...props
}: CalendarProps) => {
  const context = useSlottedContext(AriaCalendarContext);
  // Inside a DatePicker this carries the picker's field props (value,
  // onChange, segments…) for the editable DD/MM/YYYY row. It has to be
  // MERGED with our label, not replaced: providing a bare `{ 'aria-label' }`
  // detached the row from the picker, so it rendered empty placeholders
  // beside a selected date and typing into it changed nothing.
  const dateFieldContext = useSlottedContext(AriaDateFieldContext);
  const dateFieldProps = useMemo(
    () => ({ ...dateFieldContext, 'aria-label': 'Date' }),
    [dateFieldContext]
  );

  const ContextWrapper = context ? Fragment : CalendarContextProvider;

  return (
    <ContextWrapper>
      <AriaCalendar
        {...props}
        className={state =>
          cx(
            'flex flex-col',
            compact ? 'gap-2' : 'gap-3',
            typeof className === 'function' ? className(state) : className
          )
        }
      >
        <header className='flex items-center justify-between'>
          <Button
            slot='previous'
            iconLeading={ChevronLeft}
            size='sm'
            color='tertiary'
            className='size-8'
          />
          <AriaHeading className='text-sm font-semibold text-fg-secondary' />
          <Button
            slot='next'
            iconLeading={ChevronRight}
            size='sm'
            color='tertiary'
            className='size-8'
          />
        </header>

        {!hideDateInput && (
          <div className='flex gap-3'>
            <AriaDateFieldContext value={dateFieldProps}>
              <DateInput className='flex-1' />
            </AriaDateFieldContext>
            <PresetButton value={today(getLocalTimeZone())}>Today</PresetButton>
          </div>
        )}

        <AriaCalendarGrid weekdayStyle='short' className='w-max'>
          <AriaCalendarGridHeader
            className={
              compact
                ? 'border-b-2 border-transparent'
                : 'border-b-4 border-transparent'
            }
          >
            {day => (
              <AriaCalendarHeaderCell className='p-0'>
                <div className='flex size-10 items-center justify-center text-sm font-medium text-secondary'>
                  {day.slice(0, 2)}
                </div>
              </AriaCalendarHeaderCell>
            )}
          </AriaCalendarGridHeader>
          <AriaCalendarGridBody
            className={cx(
              '[&_td]:p-0 [&_tr]:border-transparent [&_tr:last-of-type]:border-none',
              compact ? '[&_tr]:border-b-2' : '[&_tr]:border-b-4'
            )}
          >
            {date => (
              <CalendarCell
                date={date}
                isHighlighted={highlightedDates?.some(
                  highlightedDate => date.compare(highlightedDate) === 0
                )}
              />
            )}
          </AriaCalendarGridBody>
        </AriaCalendarGrid>
      </AriaCalendar>
    </ContextWrapper>
  );
};
